import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Building2,
  UserCircle,
  Shield,
  ChevronDown,
  Check,
  RefreshCw
} from 'lucide-react';

const CONTEXT_ICONS = {
  kr_admin: Shield,
  agency: Building2,
  agent: UserCircle,
};

const CONTEXT_COLORS = {
  kr_admin: 'text-amber-400',
  agency: 'text-blue-400',
  agent: 'text-emerald-400',
};

export const ContextSwitcher = () => {
  const { activeContext, availableContexts, switchContext } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const buttonRef = useRef(null);

  // Compute dropdown position whenever it opens (or on scroll/resize)
  useEffect(() => {
    if (!open) return;

    const updatePosition = () => {
      const btn = buttonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      // Dropdown width: at least the button width, but expand to 280px for readability
      const width = Math.max(rect.width, 280);
      setMenuPos({
        left: rect.left,
        bottom: window.innerHeight - rect.top + 4, // 4px gap above button
        width,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  if (!availableContexts || availableContexts.length <= 1) return null;

  const handleSwitch = async (ctx) => {
    if (ctx.type === activeContext?.type && ctx.id === activeContext?.id) {
      setOpen(false);
      return;
    }
    setSwitching(true);
    try {
      await switchContext(ctx.type, ctx.id);
      toast.success(`Switched to ${ctx.label}`);
      setOpen(false);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSwitching(false);
    }
  };

  const activeLabel = availableContexts.find(
    c => c.type === activeContext?.type && c.id === activeContext?.id
  )?.label || 'Select Context';

  const ActiveIcon = CONTEXT_ICONS[activeContext?.type] || Shield;
  const activeColor = CONTEXT_COLORS[activeContext?.type] || 'text-slate-400';

  return (
    <div className="relative" data-testid="context-switcher">
      <button
        ref={buttonRef}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-left"
        data-testid="context-switcher-toggle"
        disabled={switching}
      >
        {switching ? (
          <RefreshCw size={16} className="text-slate-400 animate-spin" />
        ) : (
          <ActiveIcon size={16} className={activeColor} />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-slate-300 truncate">Acting As</div>
          <div className="text-xs text-slate-400 truncate">{activeLabel}</div>
        </div>
        <ChevronDown size={14} className={`text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && menuPos && createPortal(
        <>
          {/* Backdrop to capture outside clicks */}
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => setOpen(false)}
            data-testid="context-switcher-backdrop"
          />
          <div
            className="fixed z-[9999] bg-slate-800 border border-slate-700 rounded-lg shadow-2xl overflow-hidden"
            style={{
              left: `${menuPos.left}px`,
              bottom: `${menuPos.bottom}px`,
              width: `${menuPos.width}px`,
              maxHeight: '60vh',
              overflowY: 'auto',
            }}
            data-testid="context-switcher-dropdown"
          >
            <div className="px-3 py-2 border-b border-slate-700 sticky top-0 bg-slate-800">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Switch Context</span>
            </div>
            {availableContexts.map((ctx) => {
              const Icon = CONTEXT_ICONS[ctx.type] || Shield;
              const color = CONTEXT_COLORS[ctx.type] || 'text-slate-400';
              const isActive = ctx.type === activeContext?.type && ctx.id === activeContext?.id;
              return (
                <button
                  key={`${ctx.type}-${ctx.id}`}
                  onClick={() => handleSwitch(ctx)}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-700/50 transition-colors ${
                    isActive ? 'bg-slate-700/30' : ''
                  }`}
                  data-testid={`context-option-${ctx.type}-${ctx.id}`}
                >
                  <Icon size={16} className={`${color} flex-shrink-0`} />
                  <span className="flex-1 text-xs text-slate-300 truncate">{ctx.label}</span>
                  {isActive && <Check size={14} className="text-emerald-400 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default ContextSwitcher;
